import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	input,
	output,
} from '@angular/core';
import { UniversitiesStore } from '../../data-access/+state/universities.store';
import { CommonModule, Location } from '@angular/common';
import { CloseModalSvgComponent } from '@uc/shared/svg';
import {
	CheckboxSelectComponent,
	FilterClearingToggleComponent,
	FilterPillComponent,
	FilterSearchComponent,
	StudyLevelSelectComponent,
} from '@web/shared/ui';
import {
	CheckBoxEmitData,
	Product,
	ProductEnum,
	UniversityMultiOptionQueryParam,
} from '@uc/web/shared/data-models';
import { UniversitySearchQueryParamKey } from '../../models/university-search.models';
import { generateLink } from '../../utils/url.utils';
import { UniversitySearchScrollerService } from '../../services/university-search-scroller.service';
import { CopyGeneratorService } from '../../services/copy-generator.service';
import { FILTER_CLEARING_TOGGLE_DESC } from '../../static/universities.properties';

@Component({
	selector: 'uc-universities-filter',
	imports: [
		CommonModule,
		CloseModalSvgComponent,
		CheckboxSelectComponent,
		FilterPillComponent,
		FilterSearchComponent,
		StudyLevelSelectComponent,
		FilterClearingToggleComponent,
	],
	templateUrl: './universities-filter.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UniversitiesFilterComponent {
	readonly store = inject(UniversitiesStore);
	product = input<Product>(ProductEnum.Undergraduate);
	clearingEnabled = input(false);
	closeFilter = output<boolean>();

	studyLevelSelect = output<Product>();

	studyLevel = computed(() =>
		this.product() === ProductEnum.Postgraduate
			? ProductEnum.Postgraduate
			: ProductEnum.Undergraduate,
	);

	studyLevelCopy = computed(() =>
		this._copyGeneratorSrv.getStudyLevelCopy(this.product(), this.studyLevel()),
	);

	readonly filterToggleDesc = FILTER_CLEARING_TOGGLE_DESC;

	constructor(
		private _location: Location,
		private _copyGeneratorSrv: CopyGeneratorService,
		private _scrollSrv: UniversitySearchScrollerService,
	) {}

	onStudyLevel(studyLevel: Product) {
		this.studyLevelSelect.emit(studyLevel);
	}

	onSearchTerm(searchTerm: string) {
		this.store.updateSearchTerm(searchTerm);

		let path = this._location.path();
		if (path.match(/s=[^&]+/)) {
			path = path.replace(/s=[^&]+/, `s=${searchTerm}`);
		} else {
			path += (path.includes('?') ? '&' : '?') + `s=${searchTerm}`;
		}
		this._location.go(path);
		this._scrollSrv.scroll();
	}

	updateQueryParams(event: CheckBoxEmitData, urlKey: UniversitySearchQueryParamKey) {
		const { isChecked, urlValue } = event;
		this.store.updateQueryParams(urlKey, urlValue, isChecked);
		const baseUrl = `/universities/${this.product()}`;
		const url = generateLink(baseUrl, this.store.queryParams());

		this._location.go(url);
		this._scrollSrv.scroll();
	}

	onClearingSelect(isChecked: boolean) {
		this.studyLevelSelect.emit(
			isChecked ? ProductEnum.Clearing : ProductEnum.Undergraduate,
		);
	}

	resetFilters() {
		this.store.resetQueryParams();
		this._location.go(`/universities/${this.product()}`);
		this._scrollSrv.scroll();
	}

	removePill(urlKey: string, urlValue: string) {
		const key = urlKey as UniversityMultiOptionQueryParam;
		this.store.updateQueryParams(key, urlValue, false);
		const baseUrl = `/universities/${this.product()}`;
		const url = generateLink(baseUrl, this.store.queryParams());

		this._location.go(url);
		this._scrollSrv.scroll();
	}
}
